import React, { useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { UiTable, ConfirmChangesModal } from 'src/ui';
import { Button, MuiDropdown, MuiDropdownItem } from 'src/ui/mui-ui-kit';;
;
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs-ext';
import { DATE_DAY_SMONTH_YEAR } from 'src/ui/const';
import { VendorStatus } from 'src/vendor';
import { NavLink } from 'react-router-dom';
import { startsWithCaseInsensitive } from '../../ui';

const statusIcons = {
  Initiated: ['fas', 'flag'],
  Submitted: ['fas', 'flag'],
  Processing: ['fas', 'spinner'],
  Active: ['fal', 'check'],
  'Paid Off': ['fal', 'check'],
  'Not Active': ['fas', 'flag'],
  'In Review': ['fal', 'search'],
  Archived: ['fal', 'archive'],
  Pending: ['fas', 'spinner'],
  Withdrawn: ['fal', 'minus-circle'],
  Declined: ['fal', 'ban'],
  Closed: ['fal', 'user-slash'],
  Dormant: ['fal', 'user-slash'],
  Terminated: ['fal', 'ban'],
};

const statusColors = {
  Initiated: 'grey',
  Submitted: { icon: 'danger', text: 'primary' },
  Processing: 'secondary',
  Active: { icon: 'green', text: 'primary' },
  'Paid Off': { icon: 'info', text: 'primary' },
  'Not Active': 'grey',
  'In Review': { icon: 'info', text: 'info' },
  Archived: 'grey',
  Pending: 'secondary',
  Withdrawn: 'grey',
  Declined: { icon: 'danger', text: 'primary' },
  Closed: 'grey',
  Dormant: 'grey',
  Terminated: { icon: 'danger', text: 'primary' },
};

// Note: We use MuiButton with MuiDropdown for consistent dropdown styling across the app

export function AccountsTable({
  loading,
  accounts,
  onDeleteAccount,
  onEditAccount,
  onApproveAccount,
  onDenyAccount,
  canAddAccount,
  onSubmitToBase,
  onEditStatus,
  showEllipsis,
  hasAssignAgentsPage,
}) {
  const [isApproving, setIsApproving] = useState(false);
  const [isDenying, setIsDenying] = useState(false);
  const onCancelStatusChange = React.useCallback(() => {
    setIsApproving(false);
    setIsDenying(false);
  }, [setIsApproving, setIsDenying]);

  const onConfirmStatusChange = React.useCallback(() => {
    if (isApproving) {
      onCancelStatusChange();
      return onApproveAccount(isApproving);
    }
    onCancelStatusChange();
    return onDenyAccount(isDenying);
  }, [
    isApproving,
    isDenying,
    onCancelStatusChange,
    onApproveAccount,
    onDenyAccount,
  ]);
  const modalTitle = useMemo(() => {
    const { businessName } = isApproving || isDenying || { businessName: '' };
    return `${businessName}'s Account`;
  }, [isApproving, isDenying]);

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Account name',
        filterMethod: (_filter, row) => {
          return [
            row._original.businessName,
            ...row._original.dbas.map(dba => dba.name),
          ].some(v => v.toLowerCase().includes(_filter.value.toLowerCase()));
        },
        Cell: row => (
          <div className="account-name">
            <VendorStatus
              isApproved={row.original.status === 'Active'}
              isDenied={row.original.status === 'Submitted'}
            />
            {/* eslint-disable-next-line */}
            <a
              className="edit-link"
              onClick={() => onEditAccount(row.original)}
            >
              {row.original.businessName}
            </a>
            {row.original.dbas.length > 0 && (
              <div className="dbas">
                {row.original.dbas.map(dba => dba.name).join(', ')}
              </div>
            )}
          </div>
        ),
      },
      {
        Header: 'Primary Agent',
        accessor: 'primaryAgents',
        filterMethod: (_filter, row) => {
          return [
            ...row._original.secondaryAgents.map(dba => dba),
            ...row._original.primaryAgents.map(dba => dba),
          ].some(v => v.toLowerCase().includes(_filter.value.toLowerCase()));
        },
        Cell: row => (
          <div>
            {row.original.primaryAgents.length > 0 && (
              <div className="has-text-grey-medium-dark">
                {' '}
                {row.original.primaryAgents.join(', ')}
              </div>
            )}
          </div>
        ),
      },
      {
        Header: 'Status',
        accessor: 'status',
        filterMethod: startsWithCaseInsensitive,
        Cell: row => {
          const icon = statusIcons[row.value] || null;
          const color = statusColors[row.value];
          const spanIconClass = `icon has-text-${color.icon || color}`;
          const mainClass = `has-text-${color.text || color}`;
          const isSpinning =
            row.value === 'Processing' || row.value === 'Pending';

          return (
            <div className={mainClass}>
              <span className={spanIconClass}>
                <FontAwesomeIcon icon={icon} spin={isSpinning} />
              </span>
              {row.value}
            </div>
          );
        },
      },
      {
        Header: 'Date initiated',
        accessor: 'initiated',
        Cell: row => (
          <>
            {dayjs(row.value)
              .utc()
              .format(DATE_DAY_SMONTH_YEAR)}
          </>
        ),
      },

      {
        Header: '',
        accessor: 'id',
        sortable: false,
        width: 62,
        resizable: false,
        filterable: false,
        Cell: row => (
          <>
            {showEllipsis && (
              <MuiDropdown
                isAlign="right"
                renderTrigger={
                  <Button isColor="white">
                    <span className="icon">
                      <FontAwesomeIcon
                        icon={['fal', 'ellipsis-v']}
                        className="has-text-grey"
                        size="lg"
                      />
                    </span>
                  </Button>
                }
              >
                  {onEditAccount && (
                    <MuiDropdownItem
                      tag="a"
                      onClick={() => onEditAccount(row.original)}
                    >
                      Edit
                    </MuiDropdownItem>
                  )}
                  {onSubmitToBase && (
                    <MuiDropdownItem
                      tag="a"
                      onClick={() => onSubmitToBase(row.original)}
                    >
                      Submit to Base
                    </MuiDropdownItem>
                  )}

                  <MuiDropdownItem
                    tag="a"
                    onClick={() => onEditStatus(row.original)}
                  >
                    Set Status
                  </MuiDropdownItem>
                  {hasAssignAgentsPage && (
                    <MuiDropdownItem
                      tag={NavLink}
                      to={`/accounts/assign-agent/${row.original.id}`}
                    >
                      Assign Agents
                    </MuiDropdownItem>
                  )}

                  {onDeleteAccount && (
                    <MuiDropdownItem
                      tag="a"
                      onClick={() => onDeleteAccount(row.original)}
                    >
                      Archive
                    </MuiDropdownItem>
                  )}
              </MuiDropdown>
            )}
          </>
        ),
      },
    ],
    [
      showEllipsis,
      hasAssignAgentsPage,
      onEditAccount,
      onSubmitToBase,
      onEditStatus,
      onDeleteAccount,
    ]
  );

  return (
    <>
      <UiTable
        useTablePagination
        filterable
        className="accounts-table -striped -highlight"
        columns={tableColumns}
        loading={loading}
        data={accounts}
        resolveData={data =>
          data.map(account => ({
            ...account,
          }))
        }
        getTheadThProps={() => ({
          className: 'has-text-left',
        })}
        title="Account"
        emptyTo={canAddAccount ? '/accounts/add' : null}
        hideNext
        hidePrev
        hideButton={!canAddAccount}
      />
      <ConfirmChangesModal
        title={modalTitle}
        isActive={!!isApproving || !!isDenying}
        onClickCancel={onCancelStatusChange}
        onClickSubmit={onConfirmStatusChange}
      />
    </>
  );
}

AccountsTable.defaultProps = {
  onDeleteAccount: null,
  onEditAccount: null,
  onApproveAccount: null,
  onDenyAccount: null,
  canAddAccount: true,
  onSubmitToBase: null,
  onEditStatus: null,
};

AccountsTable.propTypes = {
  loading: propTypes.bool.isRequired,
  accounts: propTypes.arrayOf(propTypes.object).isRequired,
  onDeleteAccount: propTypes.func,
  onEditAccount: propTypes.func,
  onApproveAccount: propTypes.func,
  onDenyAccount: propTypes.func,
  canAddAccount: propTypes.bool,
  onSubmitToBase: propTypes.func,
  onEditStatus: propTypes.func,
  showEllipsis: propTypes.bool.isRequired,
  hasAssignAgentsPage: propTypes.bool.isRequired,
};
